import type { LucideIcon, LucideProps } from 'lucide-react';
import { forwardRef } from 'react';

/**
 * WhatsApp's own mark - the handset in a speech bubble - as one filled path on
 * a 24x24 grid.
 *
 * The same glyph the storefront's social row draws, defined here because the
 * rest of the app needs it too and neither place should have to import the
 * other. Anywhere the product says "WhatsApp" - a way to pay, a sidebar entry,
 * a campaign page - this is the mark to draw. What stood here before was
 * Lucide's `MessageCircle`, which is an interface icon meaning "chat": it does
 * not tell a shopper which app the button is about to open, and next to a
 * Razorpay card and a rupee note it was the one payment method not wearing its
 * own badge.
 *
 * Trademark stays WhatsApp's. This is the mark as they publish it, undistorted
 * and unrecoloured, naming the service it belongs to - which is the use their
 * brand guidelines are written for.
 */
export const WHATSAPP_GLYPH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z';

/**
 * The mark, drop-in wherever a Lucide icon stood.
 *
 * Takes `LucideProps` and is typed as a `LucideIcon` so it can sit in the icon
 * slots the app already has - a `Record<Method, LucideIcon>`, a nav entry -
 * without those learning about a second kind of icon. Filled with
 * `currentColor`, so every place that already tints its icons keeps tinting
 * this one, and `size-4` sizes it like the rest of the row.
 *
 * The two stroke props Lucide accepts are swallowed rather than forwarded:
 * this mark is filled, not stroked, so they have nothing to act on and React
 * would only warn about the unknown attributes.
 */
export const WhatsAppIcon = forwardRef<SVGSVGElement, LucideProps>(function WhatsAppIcon(
  { size = 24, color, strokeWidth: _strokeWidth, absoluteStrokeWidth: _absoluteStrokeWidth, ...props },
  ref,
) {
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color ?? 'currentColor'}
      aria-hidden
      focusable="false"
      {...props}
    >
      <path d={WHATSAPP_GLYPH} />
    </svg>
  );
}) as unknown as LucideIcon;
